/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useState} from 'react';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import {corruptedFile, roundDownload} from '../../../assets/images/images';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  PrimaryButton,
  SecondaryButton,
  TableLayout,
  TopupModal,
  TeamesDropdown as Dropdown,
} from '../../../components';
import PaymentLimitModal from './PaymentLimitModal';
import * as Actions from '../../../store/actions';
import {
  formatNumberToCurrency,
  formatUsd,
} from '../../../helpers/formatNumberToCurrency';
import BillingsLayout from './BillingsLayout';
import {parseUrlParams} from '../../../helpers';
import {PageContext} from '../../../helpers/context';
import {generateBillingPDF} from './generateBillingPDF';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import CardSummary from 'components/CardSummary';
import {Menu} from '@headlessui/react';
import {generateTransactionReceipt} from './generateTransactionReceipt';

function SettingsBilling({
  children,
  auth: {billings, loading, userDetails, updated},
  apps: {mAppLoading},
  getBillings,
  exportBillings,
  breadCrumbText,
}) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [openFilter, setOpenFilter] = useState(false);
  const [openLimit, setOpenLimit] = useState(false);
  const [openTopup, setOpenTopup] = useState(false);
  const [logoDataURL, setLogoDataURL] = useState(null);

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('billings');
  }, [setPageName]);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const logoUrl = localStorage.getItem('companyLogo');
        if (logoUrl) {
          const dataURL = await getImageDataURL(logoUrl);
          setLogoDataURL(dataURL);
        }
      } catch (error) {
        console.error('Error loading logo:', error);
        setLogoDataURL(null);
      }
    };

    loadLogo();
  }, []);

  const getImageDataURL = async imageSource => {
    if (!imageSource) return null;
    if (imageSource.startsWith('data:')) return imageSource;
    if (imageSource.startsWith('/static')) return null;

    try {
      const response = await fetch(imageSource);
      const blob = await response.blob();
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading image:', error);
      return null;
    }
  };

  useEffect(() => {
    const apiParams = parseUrlParams();
    apiParams.limit = limit;
    apiParams.page = currentPage;
    if (apiParams.type) {
      apiParams.type =
        apiParams.type.charAt(0).toUpperCase() + apiParams.type.slice(1);
    }
    getBillings(apiParams);
  }, [currentPage, limit, updated, getBillings]);

  const pageClick = selected => {
    setCurrentPage(selected);
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', selected);
    navigate(`?${searchParams.toString()}`);
  };

  const filterOptions = [
    {
      key: 'type',
      label: 'Type',
      type: 'select',
      options: [
        {value: 'Paystack', label: 'Paystack'},
        {value: 'Transfer', label: 'Transfer'},
      ],
    },
  ];

  const handleFilterChange = newFilters => {
    const filteredOptions = newFilters;
    if (filteredOptions.dateRange) {
      filteredOptions.start = filteredOptions.dateRange.start;
      filteredOptions.end = filteredOptions.dateRange.end;
      delete filteredOptions.dateRange;
    }
    if (filteredOptions.type) {
      filteredOptions.type =
        filteredOptions.type.charAt(0).toUpperCase() +
        filteredOptions.type.slice(1);
    }
    setCurrentPage(1);
    const searchParams = new URLSearchParams();
    Object.entries(filteredOptions).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    navigate(`?${searchParams.toString()}`);
    getBillings({page: 1, limit, ...filteredOptions});
  };

  const canTopUpBilling = userDetails?.permissions?.billingPermissions?.topup;
  const canManageThreshold =
    userDetails?.permissions?.billingPermissions?.manageThreshold;
  const canExportHistory =
    userDetails?.permissions?.billingPermissions?.exportHistory;

  const handleDownload = async (billing, actionType) => {
    const currency = userDetails?.company?.currency || 'NGN';
    const userName = `${userDetails?.first_name} ${userDetails?.last_name}`;
    const fileName = `${
      actionType === 'invoice' ? 'transaction_invoice' : 'transaction_receipt'
    }_${billing.reference_number}`;
    const companyName = userDetails?.company?.name;

    if (!companyName) {
      console.error('Company name is undefined!');
      return;
    }

    const generateFunction =
      actionType === 'invoice'
        ? generateBillingPDF
        : generateTransactionReceipt;

    generateFunction(
      [billing],
      currency,
      userName,
      fileName,
      logoDataURL,
      companyName,
    );
  };

  const actionMenuItems = billing => [
    {
      icon: roundDownload,
      label: 'Download Invoice',
      action: () => handleDownload(billing, 'invoice'),
    },
    {
      icon: roundDownload,
      label: 'Download Receipt',
      action: () => handleDownload(billing, 'receipt'),
    },
  ];

  const isFiltered =
    !loading &&
    billings?.api_logs?.length === 0 &&
    Object.keys(parseUrlParams()).length > 2;

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">
          {breadCrumbText || 'Transaction History'}
        </p>
      }
    >
      <PaymentLimitModal open={openLimit} setOpen={setOpenLimit} />
      <TopupModal open={openTopup} setOpen={setOpenTopup} />
      <div className="flex items-center justify-end gap-2 flex-wrap py-2 mx-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <PrimaryButton
          buttonText="Top up wallet"
          onClick={() => setOpenTopup(!openTopup)}
          disabled={!canTopUpBilling}
        />
        <SecondaryButton
          buttonText="Manage threshold"
          onClick={() => setOpenLimit(!openLimit)}
          disabled={!canManageThreshold}
        />
      </div>

      <div className="grid grid-cols-1 items-center pt-6">
        <CardSummary
          title="Wallet balance"
          body={
            userDetails?.company?.currency === 'USD'
              ? formatUsd(userDetails?.company?.wallet_balance / 100 ?? 0)
              : formatNumberToCurrency(
                  userDetails?.company?.wallet_balance / 100 ?? 0,
                  userDetails?.company?.currency,
                )
          }
          threshold={
            userDetails?.company?.currency === 'USD'
              ? formatUsd(userDetails?.company?.wallet_limit / 100)
              : formatNumberToCurrency(
                  userDetails?.company?.wallet_limit / 100,
                  userDetails?.company?.currency,
                )
          }
        />
      </div>

      <BillingsLayout>
        {children ?? (
          <section>
            <div className="flex items-center justify-end gap-2 flex-wrap py-2 mx-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
              <SecondaryButton
                buttonText="Filter"
                onClick={() => setOpenFilter(prev => !prev)}
              />
              <PrimaryButton
                onClick={() => {
                  const apiParams = parseUrlParams();
                  apiParams.limit = limit;
                  if (apiParams.type) {
                    apiParams.type =
                      apiParams.type.charAt(0).toUpperCase() +
                      apiParams.type.slice(1);
                  }
                  exportBillings(apiParams);
                }}
                buttonText="Export"
                loading={mAppLoading}
                disabled={!canExportHistory}
              />
            </div>
            <div
              className={`filter-slide-container ${openFilter ? 'open' : ''}`}
            >
              <FilterV2
                filters={filterOptions}
                onFilterChange={handleFilterChange}
              />
            </div>
            <TableLayout negativeMargins>
              <thead className="font-semibold uppercase">
                <tr className="bg-white80">
                  <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                    <div className="flex items-end gap-1">
                      <span>Amount</span>
                    </div>
                  </th>
                  <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                    <div className="flex items-end gap-1">
                      <span>VAT</span>
                    </div>
                  </th>
                  <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                    <div className="flex items-end gap-1">
                      <span>Total Amount</span>
                    </div>
                  </th>
                  <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                    <div className="flex items-end gap-1">
                      <span>Reference Number</span>
                    </div>
                  </th>
                  <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                    <div className="flex items-end gap-1">
                      <span>Status</span>
                    </div>
                  </th>
                  <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                    <div className="flex items-end gap-1">
                      <span>Payment Type</span>
                    </div>
                  </th>
                  <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                    <div className="flex items-end gap-1">
                      <span>Date Created</span>
                    </div>
                  </th>
                  <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={8}>
                    {loading ? (
                      <Loader height={45} />
                    ) : !loading &&
                      (!billings || billings?.api_logs?.length === 0) ? (
                      isFiltered ? (
                        <EmptyState
                          body="This filter did not return any data, try using different values."
                          src={corruptedFile}
                          noBtn
                          height={45}
                          customBtn={
                            <button
                              onClick={() => {
                                navigate('');
                                setCurrentPage(1);
                                getBillings({limit, page: 1});
                              }}
                              className="text-brandBlue p-4 text-sm font-medium"
                            >
                              Update preferences
                            </button>
                          }
                        />
                      ) : (
                        <EmptyState
                          height={45}
                          body="No data found"
                          src={corruptedFile}
                          noBtn
                        />
                      )
                    ) : null}
                  </td>
                </tr>
              </tbody>
              <tbody className="text-sm bg-white">
                {!loading &&
                  billings?.api_logs?.map((billing, i) => (
                    <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                      <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                        {userDetails?.company &&
                        billing?.amount !== undefined &&
                        billing?.amount !== null
                          ? formatNumberToCurrency(
                              billing.amount / 100,
                              userDetails?.company?.currency,
                            )
                          : '-'}
                      </td>
                      <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                        {userDetails?.company &&
                        billing?.vat !== undefined &&
                        billing?.vat !== null
                          ? formatNumberToCurrency(
                              billing.vat / 100,
                              userDetails?.company?.currency,
                            )
                          : '-'}
                      </td>
                      <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                        {userDetails?.company &&
                        billing?.total_amount !== undefined &&
                        billing?.total_amount !== null
                          ? formatNumberToCurrency(
                              billing.total_amount / 100,
                              userDetails?.company?.currency,
                            )
                          : '-'}
                      </td>
                      <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                        {billing?.reference_number || '-'}
                      </td>
                      <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                        <div
                          className={`px-2 py-1 text-xs font-medium uppercase rounded-full w-fit ${
                            billing?.status?.toLowerCase() === 'failed'
                              ? 'bg-red text-white'
                              : billing?.status?.toLowerCase() === 'successful'
                              ? 'bg-success20 text-success'
                              : billing?.status?.toLowerCase() === 'pending'
                              ? 'bg-warning'
                              : 'bg-disabled'
                          }`}
                        >
                          {billing?.status || '-'}
                        </div>
                      </td>
                      <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                        {billing?.type || '-'}
                      </td>
                      <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                        {billing?.createdAt
                          ? moment(billing.createdAt).format(
                              'Do MMM, YYYY, hh:mmA',
                            )
                          : '-'}
                      </td>
                      <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                        <div className="flex justify-center">
                          <Dropdown topPosition="!p-0" gap="gap-0">
                            {actionMenuItems(billing).map(item => (
                              <Menu.Item key={item.label}>
                                {() => (
                                  <button
                                    className="z-40 p-3 flex items-center gap-4 w-full font-medium text-grey-500 hover:bg-grey-50 text-tiny"
                                    onClick={item.action}
                                  >
                                    <img src={item.icon} alt="" />
                                    {item.label}
                                  </button>
                                )}
                              </Menu.Item>
                            ))}
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </TableLayout>

            {!loading && billings?.api_logs?.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
                <PageNumber
                  currentPage={+currentPage}
                  limit={limit}
                  totalCount={billings?.totalRecords || 0}
                />
                <div className="flex items-center gap-5">
                  <PageLimit
                    onLimitChange={newLimit => {
                      setLimit(newLimit);
                      setCurrentPage(1);
                    }}
                    total={billings?.totalRecords}
                    length={billings?.api_logs?.length}
                    limit={limit}
                  />
                  <Pagination
                    total={Math.ceil((billings?.totalRecords || 0) / limit)}
                    current={+currentPage}
                    onPageChange={activePage => {
                      pageClick(activePage);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 sm:hidden">
              <SecondaryButton
                buttonText="Create payment limit"
                className="w-full"
                onClick={() => setOpenLimit(!openLimit)}
                disabled={!canManageThreshold}
              />
              <PrimaryButton
                buttonText="Top Up Wallet"
                className="w-full"
                onClick={() => setOpenTopup(!openTopup)}
                disabled={!canTopUpBilling}
              />
            </div>
          </section>
        )}
      </BillingsLayout>
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(SettingsBilling);
